// Vietnam location catalogs — single source of truth for province + district
// pickers across mobile/web/API. Province codes are short stable identifiers
// persisted in DB (Prisma enum `ProvinceCode`); never rename without a
// migration. District `value` strings are still free-form labels — current
// district lives in `workerProfile.currentDistrict` as a plain string.

export const VN_PROVINCES = [
  { code: "HCM", name: "TP. Hồ Chí Minh" },
  { code: "HN", name: "Hà Nội" },
  { code: "HP", name: "Hải Phòng" },
  { code: "DNG", name: "Đà Nẵng" },
  { code: "CT", name: "Cần Thơ" },
  { code: "AG", name: "An Giang" },
  { code: "BG", name: "Bắc Giang" },
  { code: "BK", name: "Bắc Kạn" },
  { code: "BL", name: "Bạc Liêu" },
  { code: "BN", name: "Bắc Ninh" },
  { code: "BT", name: "Bến Tre" },
  { code: "BD", name: "Bình Dương" },
  { code: "BDH", name: "Bình Định" },
  { code: "BP", name: "Bình Phước" },
  { code: "BTH", name: "Bình Thuận" },
  { code: "BRVT", name: "Bà Rịa - Vũng Tàu" },
  { code: "CM", name: "Cà Mau" },
  { code: "CB", name: "Cao Bằng" },
  { code: "DL", name: "Đắk Lắk" },
  { code: "DN", name: "Đắk Nông" },
  { code: "DBN", name: "Điện Biên" },
  { code: "DNI", name: "Đồng Nai" },
  { code: "DT", name: "Đồng Tháp" },
  { code: "GL", name: "Gia Lai" },
  { code: "HG", name: "Hà Giang" },
  { code: "HNM", name: "Hà Nam" },
  { code: "HT", name: "Hà Tĩnh" },
  { code: "HD", name: "Hải Dương" },
  { code: "HGG", name: "Hậu Giang" },
  { code: "HB", name: "Hòa Bình" },
  { code: "HY", name: "Hưng Yên" },
  { code: "KH", name: "Khánh Hòa" },
  { code: "KG", name: "Kiên Giang" },
  { code: "KT", name: "Kon Tum" },
  { code: "LC", name: "Lai Châu" },
  { code: "LDG", name: "Lâm Đồng" },
  { code: "LS", name: "Lạng Sơn" },
  { code: "LCA", name: "Lào Cai" },
  { code: "LA", name: "Long An" },
  { code: "NDG", name: "Nam Định" },
  { code: "NA", name: "Nghệ An" },
  { code: "NB", name: "Ninh Bình" },
  { code: "NT", name: "Ninh Thuận" },
  { code: "PT", name: "Phú Thọ" },
  { code: "PY", name: "Phú Yên" },
  { code: "QB", name: "Quảng Bình" },
  { code: "QN", name: "Quảng Nam" },
  { code: "QNG", name: "Quảng Ngãi" },
  { code: "QNH", name: "Quảng Ninh" },
  { code: "QT", name: "Quảng Trị" },
  { code: "ST", name: "Sóc Trăng" },
  { code: "SL", name: "Sơn La" },
  { code: "TN", name: "Tây Ninh" },
  { code: "TB", name: "Thái Bình" },
  { code: "TNG", name: "Thái Nguyên" },
  { code: "TH", name: "Thanh Hóa" },
  { code: "TTH", name: "Thừa Thiên Huế" },
  { code: "TG", name: "Tiền Giang" },
  { code: "TV", name: "Trà Vinh" },
  { code: "TQ", name: "Tuyên Quang" },
  { code: "VL", name: "Vĩnh Long" },
  { code: "VP", name: "Vĩnh Phúc" },
  { code: "YB", name: "Yên Bái" },
] as const;

export type ProvinceCode = (typeof VN_PROVINCES)[number]["code"];
export const PROVINCE_CODES = VN_PROVINCES.map(
  (p) => p.code,
) as unknown as readonly [ProvinceCode, ...ProvinceCode[]];
export const PROVINCE_NAME_VI: Record<ProvinceCode, string> =
  Object.fromEntries(VN_PROVINCES.map((p) => [p.code, p.name])) as Record<
    ProvinceCode,
    string
  >;

// ─── Curated province → district list for the location picker ────────
// Coverage prioritises the labor markets we ship to first (TP.HCM, Hà Nội,
// Đà Nẵng, Bình Dương, Đồng Nai). District `value` is the canonical string
// persisted to `currentDistrict` ("Quận 1, TP.HCM").

export type DistrictOption = {
  id: string;
  label: string;
  value: string;
};

export type ProvinceGroup = {
  id: string;
  name: string;
  districts: DistrictOption[];
};

function district(
  prefix: string,
  short: string,
  full: string,
  province: string,
): DistrictOption {
  return {
    id: `${prefix}-${short}`,
    label: full,
    value: `${full}, ${province}`,
  };
}

export const PROVINCE_GROUPS: ProvinceGroup[] = [
  {
    id: "hcm",
    name: "TP. Hồ Chí Minh",
    districts: [
      district("hcm", "q1", "Quận 1", "TP.HCM"),
      district("hcm", "q3", "Quận 3", "TP.HCM"),
      district("hcm", "q4", "Quận 4", "TP.HCM"),
      district("hcm", "q5", "Quận 5", "TP.HCM"),
      district("hcm", "q6", "Quận 6", "TP.HCM"),
      district("hcm", "q7", "Quận 7", "TP.HCM"),
      district("hcm", "q8", "Quận 8", "TP.HCM"),
      district("hcm", "q10", "Quận 10", "TP.HCM"),
      district("hcm", "q11", "Quận 11", "TP.HCM"),
      district("hcm", "q12", "Quận 12", "TP.HCM"),
      district("hcm", "bt", "Quận Bình Thạnh", "TP.HCM"),
      district("hcm", "pn", "Quận Phú Nhuận", "TP.HCM"),
      district("hcm", "tb", "Quận Tân Bình", "TP.HCM"),
      district("hcm", "tp", "Quận Tân Phú", "TP.HCM"),
      district("hcm", "gv", "Quận Gò Vấp", "TP.HCM"),
      district("hcm", "btn", "Quận Bình Tân", "TP.HCM"),
      district("hcm", "tdc", "TP. Thủ Đức", "TP.HCM"),
      district("hcm", "bc", "Huyện Bình Chánh", "TP.HCM"),
      district("hcm", "hm", "Huyện Hóc Môn", "TP.HCM"),
      district("hcm", "cg", "Huyện Củ Chi", "TP.HCM"),
      district("hcm", "nb", "Huyện Nhà Bè", "TP.HCM"),
      district("hcm", "cgi", "Huyện Cần Giờ", "TP.HCM"),
      // ─── HCM 2025 reorganization (Nghị quyết 1685/NQ-UBTVQH15, hiệu lực
      // 01/07/2025): 168 phường/xã/đặc khu mới sau khi sáp nhập Bình Dương +
      // Bà Rịa - Vũng Tàu. Giữ song song với quận/huyện cũ vì giấy phép kinh
      // doanh của user có thể ghi theo địa giới cũ hoặc mới. Thứ tự theo mã
      // hành chính chính thức (BRVT → BD → trung tâm → ngoại thành).
      // Source: phucanhle/vn-xaphuong-2025 (danhmucxaphuong.json).
      district("hcm-w", "71701001", "Phường Vũng Tàu", "TP.HCM"),
      district("hcm-w", "71701002", "Phường Tam Thắng", "TP.HCM"),
      district("hcm-w", "71701003", "Phường Rạch Dừa", "TP.HCM"),
      district("hcm-w", "71701004", "Phường Phước Thắng", "TP.HCM"),
      district("hcm-w", "71703005", "Phường Bà Rịa", "TP.HCM"),
      district("hcm-w", "71703006", "Phường Long Hương", "TP.HCM"),
      district("hcm-w", "71709007", "Phường Phú Mỹ", "TP.HCM"),
      district("hcm-w", "71703008", "Phường Tam Long", "TP.HCM"),
      district("hcm-w", "71709009", "Phường Tân Thành", "TP.HCM"),
      district("hcm-w", "71709010", "Phường Tân Phước", "TP.HCM"),
      district("hcm-w", "71709011", "Phường Tân Hải", "TP.HCM"),
      district("hcm-w", "71709012", "Xã Châu Pha", "TP.HCM"),
      district("hcm-w", "71705013", "Xã Ngãi Giao", "TP.HCM"),
      district("hcm-w", "71705014", "Xã Bình Giã", "TP.HCM"),
      district("hcm-w", "71705015", "Xã Kim Long", "TP.HCM"),
      district("hcm-w", "71705016", "Xã Châu Đức", "TP.HCM"),
      district("hcm-w", "71705017", "Xã Xuân Sơn", "TP.HCM"),
      district("hcm-w", "71705018", "Xã Nghĩa Thành", "TP.HCM"),
      district("hcm-w", "71707019", "Xã Hồ Tràm", "TP.HCM"),
      district("hcm-w", "71707020", "Xã Xuyên Mộc", "TP.HCM"),
      district("hcm-w", "71707021", "Xã Hòa Hội", "TP.HCM"),
      district("hcm-w", "71707022", "Xã Bàu Lâm", "TP.HCM"),
      district("hcm-w", "71712023", "Xã Phước Hải", "TP.HCM"),
      district("hcm-w", "71712024", "Xã Long Hải", "TP.HCM"),
      district("hcm-w", "71712025", "Xã Đất Đỏ", "TP.HCM"),
      district("hcm-w", "71712026", "Xã Long Điền", "TP.HCM"),
      district("hcm-w", "71713027", "Đặc khu Côn Đảo", "TP.HCM"),
      district("hcm-w", "71109028", "Phường Đông Hoà", "TP.HCM"),
      district("hcm-w", "71109029", "Phường Dĩ An", "TP.HCM"),
      district("hcm-w", "71109030", "Phường Tân Đông Hiệp", "TP.HCM"),
      district("hcm-w", "71107031", "Phường Thuận An", "TP.HCM"),
      district("hcm-w", "71107032", "Phường Thuận Giao", "TP.HCM"),
      district("hcm-w", "71107033", "Phường Bình Hoà", "TP.HCM"),
      district("hcm-w", "71107034", "Phường Lái Thiêu", "TP.HCM"),
      district("hcm-w", "71107035", "Phường An Phú", "TP.HCM"),
      district("hcm-w", "71101036", "Phường Bình Dương", "TP.HCM"),
      district("hcm-w", "71101037", "Phường Chánh Hiệp", "TP.HCM"),
      district("hcm-w", "71101038", "Phường Thủ Dầu Một", "TP.HCM"),
      district("hcm-w", "71101039", "Phường Phú Lợi", "TP.HCM"),
      district("hcm-w", "71105040", "Phường Vĩnh Tân", "TP.HCM"),
      district("hcm-w", "71105041", "Phường Bình Cơ", "TP.HCM"),
      district("hcm-w", "71105042", "Phường Tân Uyên", "TP.HCM"),
      district("hcm-w", "71105043", "Phường Tân Hiệp", "TP.HCM"),
      district("hcm-w", "71105044", "Phường Tân Khánh", "TP.HCM"),
      district("hcm-w", "71103045", "Phường Hoà Lợi", "TP.HCM"),
      district("hcm-w", "71101046", "Phường Phú An", "TP.HCM"),
      district("hcm-w", "71113047", "Phường Tây Nam", "TP.HCM"),
      district("hcm-w", "71115048", "Phường Long Nguyên", "TP.HCM"),
      district("hcm-w", "71115049", "Phường Bến Cát", "TP.HCM"),
      district("hcm-w", "71115050", "Phường Chánh Phú Hoà", "TP.HCM"),
      district("hcm-w", "71117051", "Xã Bắc Tân Uyên", "TP.HCM"),
      district("hcm-w", "71117052", "Xã Thường Tân", "TP.HCM"),
      district("hcm-w", "71111053", "Xã An Long", "TP.HCM"),
      district("hcm-w", "71111054", "Xã Phước Thành", "TP.HCM"),
      district("hcm-w", "71111055", "Xã Phước Hoà", "TP.HCM"),
      district("hcm-w", "71111056", "Xã Phú Giáo", "TP.HCM"),
      district("hcm-w", "71115057", "Xã Trừ Văn Thố", "TP.HCM"),
      district("hcm-w", "71115058", "Xã Bàu Bàng", "TP.HCM"),
      district("hcm-w", "71113059", "Xã Minh Thạnh", "TP.HCM"),
      district("hcm-w", "71113060", "Xã Long Hoà", "TP.HCM"),
      district("hcm-w", "71113061", "Xã Dầu Tiếng", "TP.HCM"),
      district("hcm-w", "71113062", "Xã Thanh An", "TP.HCM"),
      district("hcm-w", "70101063", "Phường Sài Gòn", "TP.HCM"),
      district("hcm-w", "70101064", "Phường Tân Định", "TP.HCM"),
      district("hcm-w", "70101065", "Phường Bến Thành", "TP.HCM"),
      district("hcm-w", "70101066", "Phường Cầu Ông Lãnh", "TP.HCM"),
      district("hcm-w", "70105067", "Phường Bàn Cờ", "TP.HCM"),
      district("hcm-w", "70105068", "Phường Xuân Hoà", "TP.HCM"),
      district("hcm-w", "70105069", "Phường Nhiêu Lộc", "TP.HCM"),
      district("hcm-w", "70107070", "Phường Xóm Chiếu", "TP.HCM"),
      district("hcm-w", "70107071", "Phường Khánh Hội", "TP.HCM"),
      district("hcm-w", "70107072", "Phường Vĩnh Hội", "TP.HCM"),
      district("hcm-w", "70109073", "Phường Chợ Quán", "TP.HCM"),
      district("hcm-w", "70109074", "Phường An Đông", "TP.HCM"),
      district("hcm-w", "70109075", "Phường Chợ Lớn", "TP.HCM"),
      district("hcm-w", "70111076", "Phường Bình Tây", "TP.HCM"),
      district("hcm-w", "70111077", "Phường Bình Tiên", "TP.HCM"),
      district("hcm-w", "70111078", "Phường Bình Phú", "TP.HCM"),
      district("hcm-w", "70111079", "Phường Phú Lâm", "TP.HCM"),
      district("hcm-w", "70113080", "Phường Tân Thuận", "TP.HCM"),
      district("hcm-w", "70113081", "Phường Phú Thuận", "TP.HCM"),
      district("hcm-w", "70113082", "Phường Tân Mỹ", "TP.HCM"),
      district("hcm-w", "70113083", "Phường Tân Hưng", "TP.HCM"),
      district("hcm-w", "70115084", "Phường Chánh Hưng", "TP.HCM"),
      district("hcm-w", "70115085", "Phường Phú Định", "TP.HCM"),
      district("hcm-w", "70115086", "Phường Bình Đông", "TP.HCM"),
      district("hcm-w", "70119087", "Phường Diên Hồng", "TP.HCM"),
      district("hcm-w", "70119088", "Phường Vườn Lài", "TP.HCM"),
      district("hcm-w", "70119089", "Phường Hoà Hưng", "TP.HCM"),
      district("hcm-w", "70121090", "Phường Minh Phụng", "TP.HCM"),
      district("hcm-w", "70121091", "Phường Bình Thới", "TP.HCM"),
      district("hcm-w", "70121092", "Phường Hoà Bình", "TP.HCM"),
      district("hcm-w", "70121093", "Phường Phú Thọ", "TP.HCM"),
      district("hcm-w", "70123094", "Phường Đông Hưng Thuận", "TP.HCM"),
      district("hcm-w", "70123095", "Phường Trung Mỹ Tây", "TP.HCM"),
      district("hcm-w", "70123096", "Phường Tân Thới Hiệp", "TP.HCM"),
      district("hcm-w", "70123097", "Phường Thới An", "TP.HCM"),
      district("hcm-w", "70123098", "Phường An Phú Đông", "TP.HCM"),
      district("hcm-w", "70134099", "Phường An Lạc", "TP.HCM"),
      district("hcm-w", "70134100", "Phường Tân Tạo", "TP.HCM"),
      district("hcm-w", "70134101", "Phường Bình Tân", "TP.HCM"),
      district("hcm-w", "70134102", "Phường Bình Trị Đông", "TP.HCM"),
      district("hcm-w", "70134103", "Phường Bình Hưng Hoà", "TP.HCM"),
      district("hcm-w", "70129104", "Phường Gia Định", "TP.HCM"),
      district("hcm-w", "70129105", "Phường Bình Thạnh", "TP.HCM"),
      district("hcm-w", "70129106", "Phường Bình Lợi Trung", "TP.HCM"),
      district("hcm-w", "70129107", "Phường Thạnh Mỹ Tây", "TP.HCM"),
      district("hcm-w", "70129108", "Phường Bình Quới", "TP.HCM"),
      district("hcm-w", "70125109", "Phường Hạnh Thông", "TP.HCM"),
      district("hcm-w", "70125110", "Phường An Nhơn", "TP.HCM"),
      district("hcm-w", "70125111", "Phường Gò Vấp", "TP.HCM"),
      district("hcm-w", "70125112", "Phường An Hội Đông", "TP.HCM"),
      district("hcm-w", "70125113", "Phường Thông Tây Hội", "TP.HCM"),
      district("hcm-w", "70125114", "Phường An Hội Tây", "TP.HCM"),
      district("hcm-w", "70131115", "Phường Đức Nhuận", "TP.HCM"),
      district("hcm-w", "70131116", "Phường Cầu Kiệu", "TP.HCM"),
      district("hcm-w", "70131117", "Phường Phú Nhuận", "TP.HCM"),
      district("hcm-w", "70127118", "Phường Tân Sơn Hoà", "TP.HCM"),
      district("hcm-w", "70127119", "Phường Tân Sơn Nhất", "TP.HCM"),
      district("hcm-w", "70127120", "Phường Tân Hoà", "TP.HCM"),
      district("hcm-w", "70127121", "Phường Bảy Hiền", "TP.HCM"),
      district("hcm-w", "70127122", "Phường Tân Bình", "TP.HCM"),
      district("hcm-w", "70127123", "Phường Tân Sơn", "TP.HCM"),
      district("hcm-w", "70128124", "Phường Tây Thạnh", "TP.HCM"),
      district("hcm-w", "70128125", "Phường Tân Sơn Nhì", "TP.HCM"),
      district("hcm-w", "70128126", "Phường Phú Thọ Hoà", "TP.HCM"),
      district("hcm-w", "70128127", "Phường Tân Phú", "TP.HCM"),
      district("hcm-w", "70128128", "Phường Phú Thạnh", "TP.HCM"),
      district("hcm-w", "70145129", "Phường Hiệp Bình", "TP.HCM"),
      district("hcm-w", "70145130", "Phường Thủ Đức", "TP.HCM"),
      district("hcm-w", "70145131", "Phường Tam Bình", "TP.HCM"),
      district("hcm-w", "70145132", "Phường Linh Xuân", "TP.HCM"),
      district("hcm-w", "70145133", "Phường Tăng Nhơn Phú", "TP.HCM"),
      district("hcm-w", "70145134", "Phường Long Bình", "TP.HCM"),
      district("hcm-w", "70145135", "Phường Long Phước", "TP.HCM"),
      district("hcm-w", "70145136", "Phường Long Trường", "TP.HCM"),
      district("hcm-w", "70145137", "Phường Cát Lái", "TP.HCM"),
      district("hcm-w", "70145138", "Phường Bình Trưng", "TP.HCM"),
      district("hcm-w", "70145139", "Phường Phước Long", "TP.HCM"),
      district("hcm-w", "70145140", "Phường An Khánh", "TP.HCM"),
      district("hcm-w", "70139141", "Xã Vĩnh Lộc", "TP.HCM"),
      district("hcm-w", "70139142", "Xã Tân Vĩnh Lộc", "TP.HCM"),
      district("hcm-w", "70139143", "Xã Bình Lợi", "TP.HCM"),
      district("hcm-w", "70139144", "Xã Tân Nhựt", "TP.HCM"),
      district("hcm-w", "70139145", "Xã Bình Chánh", "TP.HCM"),
      district("hcm-w", "70139146", "Xã Hưng Long", "TP.HCM"),
      district("hcm-w", "70139147", "Xã Bình Hưng", "TP.HCM"),
      district("hcm-w", "70143148", "Xã Bình Khánh", "TP.HCM"),
      district("hcm-w", "70143149", "Xã An Thới Đông", "TP.HCM"),
      district("hcm-w", "70143150", "Xã Cần Giờ", "TP.HCM"),
      district("hcm-w", "70135151", "Xã Củ Chi", "TP.HCM"),
      district("hcm-w", "70135152", "Xã Tân An Hội", "TP.HCM"),
      district("hcm-w", "70135153", "Xã Thái Mỹ", "TP.HCM"),
      district("hcm-w", "70135154", "Xã An Nhơn Tây", "TP.HCM"),
      district("hcm-w", "70135155", "Xã Nhuận Đức", "TP.HCM"),
      district("hcm-w", "70135156", "Xã Phú Hoà Đông", "TP.HCM"),
      district("hcm-w", "70135157", "Xã Bình Mỹ", "TP.HCM"),
      district("hcm-w", "70137158", "Xã Đông Thạnh", "TP.HCM"),
      district("hcm-w", "70137159", "Xã Hóc Môn", "TP.HCM"),
      district("hcm-w", "70137160", "Xã Xuân Thới Sơn", "TP.HCM"),
      district("hcm-w", "70137161", "Xã Bà Điểm", "TP.HCM"),
      district("hcm-w", "70141162", "Xã Nhà Bè", "TP.HCM"),
      district("hcm-w", "70141163", "Xã Hiệp Phước", "TP.HCM"),
      district("hcm-w", "71701164", "Xã Long Sơn", "TP.HCM"),
      district("hcm-w", "71707165", "Xã Hòa Hiệp", "TP.HCM"),
      district("hcm-w", "71707166", "Xã Bình Châu", "TP.HCM"),
      district("hcm-w", "71103167", "Phường Thới Hoà", "TP.HCM"),
      district("hcm-w", "70143168", "Xã Thạnh An", "TP.HCM"),
    ],
  },
  {
    id: "hn",
    name: "Hà Nội",
    districts: [
      district("hn", "hk", "Quận Hoàn Kiếm", "Hà Nội"),
      district("hn", "bd", "Quận Ba Đình", "Hà Nội"),
      district("hn", "dd", "Quận Đống Đa", "Hà Nội"),
      district("hn", "hbt", "Quận Hai Bà Trưng", "Hà Nội"),
      district("hn", "cg", "Quận Cầu Giấy", "Hà Nội"),
      district("hn", "tx", "Quận Thanh Xuân", "Hà Nội"),
      district("hn", "hm", "Quận Hoàng Mai", "Hà Nội"),
      district("hn", "lb", "Quận Long Biên", "Hà Nội"),
      district("hn", "nt", "Quận Nam Từ Liêm", "Hà Nội"),
      district("hn", "bt", "Quận Bắc Từ Liêm", "Hà Nội"),
      district("hn", "tay", "Quận Tây Hồ", "Hà Nội"),
      district("hn", "ha", "Quận Hà Đông", "Hà Nội"),
    ],
  },
  {
    id: "dn",
    name: "Đà Nẵng",
    districts: [
      district("dn", "hc", "Quận Hải Châu", "Đà Nẵng"),
      district("dn", "tk", "Quận Thanh Khê", "Đà Nẵng"),
      district("dn", "st", "Quận Sơn Trà", "Đà Nẵng"),
      district("dn", "nhs", "Quận Ngũ Hành Sơn", "Đà Nẵng"),
      district("dn", "lc", "Quận Liên Chiểu", "Đà Nẵng"),
      district("dn", "cl", "Quận Cẩm Lệ", "Đà Nẵng"),
      district("dn", "hv", "Huyện Hòa Vang", "Đà Nẵng"),
    ],
  },
  {
    id: "bd",
    name: "Bình Dương",
    districts: [
      district("bd", "tdm", "TP. Thủ Dầu Một", "Bình Dương"),
      district("bd", "da", "TP. Dĩ An", "Bình Dương"),
      district("bd", "tu", "TP. Thuận An", "Bình Dương"),
      district("bd", "tn", "TX. Tân Uyên", "Bình Dương"),
      district("bd", "bc", "TX. Bến Cát", "Bình Dương"),
      district("bd", "bt", "Huyện Bàu Bàng", "Bình Dương"),
      district("bd", "dt", "Huyện Dầu Tiếng", "Bình Dương"),
    ],
  },
  {
    id: "dni",
    name: "Đồng Nai",
    districts: [
      district("dni", "bh", "TP. Biên Hòa", "Đồng Nai"),
      district("dni", "lk", "TP. Long Khánh", "Đồng Nai"),
      district("dni", "na", "Huyện Nhơn Trạch", "Đồng Nai"),
      district("dni", "lt", "Huyện Long Thành", "Đồng Nai"),
      district("dni", "vc", "Huyện Vĩnh Cửu", "Đồng Nai"),
      district("dni", "th", "Huyện Trảng Bom", "Đồng Nai"),
    ],
  },
  {
    id: "hp",
    name: "Hải Phòng",
    districts: [
      district("hp", "hb", "Quận Hồng Bàng", "Hải Phòng"),
      district("hp", "le", "Quận Lê Chân", "Hải Phòng"),
      district("hp", "ng", "Quận Ngô Quyền", "Hải Phòng"),
      district("hp", "ha", "Quận Hải An", "Hải Phòng"),
      district("hp", "do", "Quận Đồ Sơn", "Hải Phòng"),
    ],
  },
  {
    id: "ct",
    name: "Cần Thơ",
    districts: [
      district("ct", "nk", "Quận Ninh Kiều", "Cần Thơ"),
      district("ct", "bt", "Quận Bình Thủy", "Cần Thơ"),
      district("ct", "cr", "Quận Cái Răng", "Cần Thơ"),
      district("ct", "om", "Quận Ô Môn", "Cần Thơ"),
    ],
  },
  {
    id: "kh",
    name: "Khánh Hòa",
    districts: [
      district("kh", "nt", "TP. Nha Trang", "Khánh Hòa"),
      district("kh", "cr", "TP. Cam Ranh", "Khánh Hòa"),
      district("kh", "nh", "TX. Ninh Hòa", "Khánh Hòa"),
    ],
  },
  {
    id: "brvt",
    name: "Bà Rịa - Vũng Tàu",
    districts: [
      district("brvt", "vt", "TP. Vũng Tàu", "Bà Rịa - Vũng Tàu"),
      district("brvt", "br", "TP. Bà Rịa", "Bà Rịa - Vũng Tàu"),
      district("brvt", "pm", "TX. Phú Mỹ", "Bà Rịa - Vũng Tàu"),
    ],
  },
  {
    id: "ld",
    name: "Lâm Đồng",
    districts: [
      district("ld", "dl", "TP. Đà Lạt", "Lâm Đồng"),
      district("ld", "bl", "TP. Bảo Lộc", "Lâm Đồng"),
    ],
  },
];

export const ALL_DISTRICTS: (DistrictOption & {
  provinceId: string;
  provinceName: string;
})[] = PROVINCE_GROUPS.flatMap((p) =>
  p.districts.map((d) => ({ ...d, provinceId: p.id, provinceName: p.name })),
);

// Lowercase, accent-insensitive search key for VN text.
export function normalizeVN(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .trim();
}
